// Editor-side checks mirroring what the /workflows API enforces, so problems
// show on the canvas before Save. Errors block saving; warnings don't.

import {
  PROSPECT_ORDER_TRIGGER,
  SIGNAL_TRIGGER,
  type DataSourceOption,
  type FieldSpec,
  type WorkflowDataType,
  type WorkflowStepType,
} from "../../../api/client.ts";
import {
  conditionsOf,
  isConditionIncomplete,
  isFieldMissing,
  isFieldOutOfRange,
  rangeText,
} from "../../../components/fields/fieldSpecs.ts";
import { createsSubject, isFirstOnSubject, stepSubjects, subjectsOf } from "./subjects.ts";
import {
  KIND_META,
  LIST_CONTRACTS,
  MAX_STEP_DEPTH,
  SUBJECT_LABELS,
  TRIGGER_ID,
  type ConditionEdge,
  type StepNode,
} from "./types.ts";

export type HintLevel = "error" | "warning";

export interface Hint {
  level: HintLevel;
  message: string;
  nodeId?: string;
  edgeId?: string;
  // With `nodeId`: about that agent step's data source, so the pill can be focused.
  dataSourceKey?: string;
  // Set on hints the panel shows in place rather than in its hint list.
  key?: typeof DATA_TYPE_UNAVAILABLE;
}

// The trigger's data type can't deliver records to this tenant (nothing connected).
export const DATA_TYPE_UNAVAILABLE = "data_type_unavailable";

const ORDERED_OPS = new Set(["gt", "gte", "lt", "lte"]);

// What is wrong with one FieldSpec value, for "<label>: <field> <problem>." hints.
function fieldProblem(f: FieldSpec, value: unknown): string | null {
  if (f.required && isFieldMissing(f, value)) return "is required";
  if (isFieldOutOfRange(f, value)) return `must be ${rangeText(f)}`;
  if (f.type === "conditions" && conditionsOf(value).some(isConditionIncomplete)) {
    return "has an incomplete condition";
  }
  return null;
}

export function validate(
  name: string,
  nodes: StepNode[],
  edges: ConditionEdge[],
  dataSources: DataSourceOption[],
  dataTypes: WorkflowDataType[],
  // Which subjects each step type runs on; the local table until it loads.
  stepTypes?: WorkflowStepType[],
): Hint[] {
  const hints: Hint[] = [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const incoming = new Map<string, ConditionEdge>();
  const outgoing = new Map<string, ConditionEdge[]>();
  for (const e of edges) {
    incoming.set(e.target, e);
    outgoing.set(e.source, [...(outgoing.get(e.source) ?? []), e]);
  }
  // What each step runs on: the trigger's subject, or the order a step made.
  const subjects = subjectsOf(nodes, edges);

  if (!name.trim()) hints.push({ level: "error", message: "Give the workflow a name." });

  // Agents chain: one first on each subject (after the trigger, or after the
  // step that made the order), further ones under an agent.
  const firstAgents = new Map<string, StepNode[]>();
  for (const n of nodes) {
    const parent = incoming.get(n.id)?.source;
    if (n.data.kind !== "agent" || !parent || !isFirstOnSubject(parent, byId.get(parent)?.data)) continue;
    firstAgents.set(parent, [...(firstAgents.get(parent) ?? []), n]);
  }
  for (const [parent, agents] of firstAgents) {
    for (const n of agents.slice(1)) {
      hints.push({
        level: "error",
        message:
          parent === TRIGGER_ID
            ? "Only one agent can run directly after the trigger; chain further agents under it."
            : "Only one agent can run directly on the order; chain further agents under it.",
        nodeId: n.id,
      });
    }
  }

  // Depth below the trigger, with a visited set in case an edge loop sneaks in.
  const depth = new Map<string, number>([[TRIGGER_ID, 0]]);
  const queue: string[] = [TRIGGER_ID];
  while (queue.length) {
    const id = queue.shift()!;
    for (const e of outgoing.get(id) ?? []) {
      if (depth.has(e.target)) continue;
      depth.set(e.target, depth.get(id)! + 1);
      queue.push(e.target);
    }
  }

  for (const n of nodes) {
    const d = n.data;
    if (d.kind !== "trigger" && !depth.has(n.id)) {
      hints.push({
        level: "error",
        message: "Unreachable step: connect it to the trigger or another step.",
        nodeId: n.id,
      });
    }
    const nodeDepth = depth.get(n.id);
    if (nodeDepth !== undefined && nodeDepth > MAX_STEP_DEPTH) {
      hints.push({
        level: "error",
        message: `Steps can be chained at most ${MAX_STEP_DEPTH} deep.`,
        nodeId: n.id,
      });
    }
    const subject = subjects.get(n.id);
    if (d.kind !== "trigger" && subject && !stepSubjects(d.kind, stepTypes).includes(subject)) {
      hints.push({
        level: "error",
        message: `${KIND_META[d.kind].label} cannot run on a ${SUBJECT_LABELS[subject]}.`,
        nodeId: n.id,
      });
    }
    const parent = incoming.get(n.id)?.source;
    const parentData = parent ? byId.get(parent)?.data : undefined;
    const onSignal = subject !== PROSPECT_ORDER_TRIGGER;

    switch (d.kind) {
      case "trigger":
        if (d.trigger_type === SIGNAL_TRIGGER) {
          if (!d.data_type) {
            hints.push({ level: "error", message: "Pick a data type.", nodeId: n.id });
          }
          if (d.match_mode !== "rules" && !d.prompt.trim()) {
            hints.push({
              level: "error",
              message: "Describe what the trigger looks for.",
              nodeId: n.id,
            });
          }
          const dataType = dataTypes.find((t) => t.slug === d.data_type);
          for (const f of dataType?.config_fields ?? []) {
            const problem = fieldProblem(f, d.source[f.name]);
            if (problem) {
              hints.push({ level: "error", message: `${f.label} ${problem}.`, nodeId: n.id });
            }
          }
          if (dataType?.unavailable_reason) {
            hints.push({
              level: "warning",
              message: `${dataType.label}: ${dataType.unavailable_reason}`,
              nodeId: n.id,
              key: DATA_TYPE_UNAVAILABLE,
            });
          }
        }
        break;
      case "agent": {
        if (parent && parent !== TRIGGER_ID && parentData?.kind !== "agent" && !createsSubject(parentData)) {
          hints.push({
            level: "error",
            message: "An agent can only be chained under the trigger, another agent or a step that creates an order.",
            nodeId: n.id,
          });
        }
        if (!d.agent_id) {
          hints.push({ level: "error", message: "Pick an agent to run.", nodeId: n.id });
          break;
        }
        if (d.contract && subject && !d.contract_subject_kinds.includes(subject)) {
          hints.push({
            level: "error",
            message: `${d.agent_name || "This agent"} cannot run on a ${SUBJECT_LABELS[subject]}.`,
            nodeId: n.id,
          });
        }
        if (
          d.contract === "contact_list" &&
          parent &&
          !(
            parentData?.kind === "agent" &&
            parentData.contract === (onSignal ? "signal_brief" : "prospect_list")
          )
        ) {
          hints.push({
            level: "warning",
            message: onSignal
                ? "Contact research finds people at the buyer the signal's research names; chain it under the research agent."
                : "Contact research works on the prospecting agent's collection; chain it under that agent.",
            nodeId: n.id,
          });
        }
        if (d.requires_instructions && !d.instructions.trim()) {
          hints.push({
            level: "error",
            message: `${d.agent_name || "The agent"} needs instructions for this step.`,
            nodeId: n.id,
          });
        }
        const cap = d.max_runs_per_day;
        if (cap != null && (!Number.isInteger(cap) || cap < 1 || cap > 1000)) {
          hints.push({
            level: "error",
            message: "Max runs per day must be between 1 and 1000.",
            nodeId: n.id,
          });
        }
        for (const [key, config] of Object.entries(d.data_sources)) {
          const option = dataSources.find((o) => o.key === key);
          // Settings left for an always-on source are dropped by the server.
          if (option?.always_on) continue;
          const label = option?.label ?? key;
          // Only once the definition is known: `allowed_data_sources` is empty until then.
          if (d.contract && !d.allowed_data_sources.includes(key)) {
            hints.push({
              level: "error",
              message: `${d.agent_name || "This agent"} does not allow the ${label} data source; remove it.`,
              nodeId: n.id,
              dataSourceKey: key,
            });
            continue;
          }
          if (!option) continue; // the catalog hasn't loaded
          for (const f of option.step_fields) {
            const value = config[f.name];
            const problem = fieldProblem(f, value);
            if (!problem) continue;
            hints.push({
              level: "error",
              message:
                f.name === "max_spend_per_run" && problem === "is required"
                  ? `Set a spend cap per run for ${label}.`
                  : `${label}: ${f.label.toLowerCase()} ${problem}.`,
              nodeId: n.id,
              dataSourceKey: key,
            });
          }
          if (!option.implicit && !option.configured) {
            hints.push({
              level: "warning",
              message: `${label} is not configured for this organization; runs will fail until it is set up.`,
              nodeId: n.id,
              dataSourceKey: key,
            });
          }
        }
        for (const option of dataSources) {
          if (!option.always_on || option.configured || !d.allowed_data_sources.includes(option.key)) continue;
          hints.push({
            level: "warning",
            message: `${option.label} is not configured for this organization; runs go without it until it is.`,
            nodeId: n.id,
            dataSourceKey: option.key,
          });
        }
        if (!d.enabled) {
          hints.push({
            level: "warning",
            message: onSignal
                ? "The agent won't run automatically; research can still be started from a signal."
                : "The agent is off; orders will only get the other steps.",
            nodeId: n.id,
          });
        }
        break;
      }
      case "add_to_prospect_list":
        if (!d.collection_id) {
          hints.push({ level: "error", message: "Pick a collection.", nodeId: n.id });
        }
        if (!onSignal) {
          if (isFirstOnSubject(parent, parentData)) {
            hints.push({
              level: "error",
              message:
                "On a prospect order, chain this under the prospecting or contact research agent, whose finds it copies.",
              nodeId: n.id,
            });
          } else if (
            parentData?.kind === "agent" &&
            parentData.contract &&
            !LIST_CONTRACTS.has(parentData.contract)
          ) {
            hints.push({
              level: "error",
              message: "On a prospect order, only an agent that fills a collection has finds to copy.",
              nodeId: n.id,
            });
          }
        }
        break;
      case "create_prospect_order": {
        const cap = d.max_orders_per_day;
        if (cap != null && (!Number.isInteger(cap) || cap < 1 || cap > 200)) {
          hints.push({ level: "error", message: "Max orders per day must be between 1 and 200.", nodeId: n.id });
        }
        const count = d.defaults.requested_count;
        if (count != null && (!Number.isInteger(count) || count < 1)) {
          hints.push({ level: "error", message: "Default count must be a whole number above 0.", nodeId: n.id });
        }
        if (!(outgoing.get(n.id) ?? []).length) {
          hints.push({
            level: "warning",
            message: "Nothing works the order yet; chain the prospecting agent under this step.",
            nodeId: n.id,
          });
        }
        break;
      }
      case "slack_message":
        if (!d.channel_id) {
          hints.push({ level: "error", message: "Pick a Slack channel.", nodeId: n.id });
        }
        if ((outgoing.get(n.id) ?? []).length) {
          hints.push({
            level: "error",
            message: "Nothing can be chained after a Slack message.",
            nodeId: n.id,
          });
        }
        break;
    }
  }

  for (const e of edges) {
    const source = byId.get(e.source)?.data;
    if (source?.kind !== "agent") continue;
    if (source.contract === "signal_brief" && !e.data?.verdicts?.length) {
      hints.push({
        level: "error",
        message: "Pick at least one verdict for this branch.",
        edgeId: e.id,
      });
    }
    const minResults = e.data?.min_results;
    if (
      LIST_CONTRACTS.has(source.contract) &&
      minResults != null &&
      (!Number.isInteger(minResults) || minResults < 0)
    ) {
      hints.push({
        level: "error",
        message: "Minimum results must be a whole number, 0 or more.",
        edgeId: e.id,
      });
    }
    if (source.contract === "structured" && e.data?.field) {
      const field = source.output_fields.find((f) => f.name === e.data?.field);
      const value = e.data.value;
      const op = e.data.op ?? "eq";
      if (!field) {
        hints.push({
          level: "error",
          message: `The agent has no result field "${e.data.field}".`,
          edgeId: e.id,
        });
      } else if (ORDERED_OPS.has(op) && field.type !== "number" && field.type !== "integer") {
        hints.push({
          level: "error",
          message: "Greater/less than only compares number fields.",
          edgeId: e.id,
        });
      } else if (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        hints.push({ level: "error", message: "Give the condition a value.", edgeId: e.id });
      }
    }
  }

  return hints;
}

export function hasErrors(hints: Hint[]): boolean {
  return hints.some((h) => h.level === "error");
}
